import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  useToast,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorMode,
} from '@chakra-ui/react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useState, useRef } from 'react';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { getColor } from '../brandConfig';

// Set up PDF.js worker - use unpkg for reliability
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const PitchDeckPage = () => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const toast = useToast();
  const presentationRef = useRef(null);
  const { colorMode } = useColorMode();

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  function onDocumentLoadError() {
    toast({
      title: "Error loading document",
      description: "Please try again later",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }

  return (
    <>
      <NavbarWithCallToAction />
      <Box
        minH="100vh"
        bg={getColor('background.main', colorMode)}
        color={getColor('text.primary', colorMode)}
        position="relative"
      >
        <Box
          position="fixed"
          top="50%"
          left={{ base: "2px", md: 6 }}
          transform="translateY(-50%)"
          zIndex={2}
        >
          <IconButton
            aria-label="Previous page"
            icon={<ChevronLeftIcon boxSize={{ base: 4, md: 8 }} />}
            onClick={() => setPageNumber(pageNumber - 1)}
            isDisabled={pageNumber <= 1}
            variant="ghost"
            rounded="full"
            size={{ base: "sm", md: "lg" }}
            sx={{
              color: 'white',
              bg: 'rgba(59, 130, 246, 0.1)',
              backdropFilter: 'blur(4px)',
              transition: 'all 0.2s',
              padding: { base: 1, md: 3 },
              _hover: {
                bg: 'rgba(59, 130, 246, 0.2)',
                transform: 'scale(1.1)',
              },
              _active: {
                bg: 'rgba(59, 130, 246, 0.3)',
              },
              _disabled: {
                opacity: 0.3,
                cursor: 'not-allowed',
                _hover: {
                  transform: 'none',
                }
              }
            }}
          />
        </Box>

        <Box
          position="fixed"
          top="50%"
          right={{ base: "2px", md: 6 }}
          transform="translateY(-50%)"
          zIndex={2}
        >
          <IconButton
            aria-label="Next page"
            icon={<ChevronRightIcon boxSize={{ base: 4, md: 8 }} />}
            onClick={() => setPageNumber(pageNumber + 1)}
            isDisabled={pageNumber >= (numPages || 0)}
            variant="ghost"
            rounded="full"
            size={{ base: "sm", md: "lg" }}
            sx={{
              color: 'white',
              bg: 'rgba(59, 130, 246, 0.1)',
              backdropFilter: 'blur(4px)',
              transition: 'all 0.2s',
              padding: { base: 1, md: 3 },
              _hover: {
                bg: 'rgba(59, 130, 246, 0.2)',
                transform: 'scale(1.1)',
              },
              _active: {
                bg: 'rgba(59, 130, 246, 0.3)',
              },
              _disabled: {
                opacity: 0.3,
                cursor: 'not-allowed',
                _hover: {
                  transform: 'none',
                }
              }
            }}
          />
        </Box>

        <Container maxW="container.xl" py={20}>
          <Alert
            status="warning"
            variant="solid"
            bg="rgba(236, 201, 75, 0.2)"
            color="yellow.200"
            borderRadius="xl"
            mb={12}
            borderWidth="1px"
            borderColor="yellow.400"
            backdropFilter="blur(4px)"
          >
            <AlertIcon color="yellow.300" />
            <Box>
              <AlertTitle fontSize="lg">Draft Content Notice</AlertTitle>
              <AlertDescription>
                This content is in draft format and subject to change. All information is currently under review and discussion.
              </AlertDescription>
            </Box>
          </Alert>

          <VStack spacing={8} textAlign="center" mb={20}>
            <Text
              sx={{
                color: getColor('primary', colorMode),
                letterSpacing: '0.13em',
                textTransform: 'uppercase',
                backgroundColor: `${getColor('primary', colorMode)}20`,
                borderRadius: '50em',
                padding: '0.7em 1.3em',
                fontSize: '0.75em',
                fontWeight: 500,
                boxShadow: `0 4px 20px ${getColor('primary', colorMode)}50`,
              }}
            >
              Pitch Deck
            </Text>
            <Heading
              size="2xl"
              color={getColor('text.primary', colorMode)}
            >
              Block52 Pitch Deck
            </Heading>
            <Text
              fontSize="xl"
              color={getColor('text.secondary', colorMode)}
              maxW="2xl"
            >
              Our vision for the future of Block52
            </Text>
          </VStack>

          <Box
            ref={presentationRef}
            p={{ base: 4, md: 8 }}
            borderRadius="xl"
            border="1px solid"
            borderColor={getColor('border.medium', colorMode)}
            bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
            mb={20}
          >
            <VStack spacing={6} align="center" width="100%">
            <Box
                width="100%"
                sx={{
                  '& .react-pdf__Document': {
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  },
                  '& .react-pdf__Page': {
                    maxWidth: '100%',
                    height: 'auto',
                    '& canvas': {
                      maxWidth: '100%!important',
                      height: 'auto!important',
                    }
                  },
                  overflowX: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <Document
                  file="/block52/2025-2-03-draft_pitch_deck.pdf"
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                >
                  <Page
                    pageNumber={pageNumber}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    scale={1.0}
                    width={window.innerWidth < 768 ? window.innerWidth - 48 : undefined}
                  />
                </Document>
              </Box>

              <Box display="flex" gap={4} alignItems="center">
                <Button
                  onClick={() => setPageNumber(pageNumber - 1)}
                  isDisabled={pageNumber <= 1}
                  size="sm"
                  px={6}
                  bg={getColor('primary', colorMode)}
                  color="white"
                  _hover={{
                    bg: getColor('primaryHover', colorMode),
                    transform: 'translateY(-2px)',
                  }}
                >
                  Previous
                </Button>
                <Text color={getColor('text.primary', colorMode)}>
                  Page {pageNumber} of {numPages}
                </Text>
                <Button
                  onClick={() => setPageNumber(pageNumber + 1)}
                  isDisabled={pageNumber >= (numPages || 0)}
                  size="sm"
                  px={6}
                  bg={getColor('primary', colorMode)}
                  color="white"
                  _hover={{
                    bg: getColor('primaryHover', colorMode),
                    transform: 'translateY(-2px)',
                  }}
                >
                  Next
                </Button>
              </Box>

              <Button
                as="a"
                href="/block52/2025-2-03-draft_pitch_deck.pdf"
                download
                size="sm"
                px={6}
                bg={getColor('primary', colorMode)}
                color="white"
                _hover={{
                  bg: getColor('primaryHover', colorMode),
                  transform: 'translateY(-2px)',
                }}
              >
                Download Pitch Deck
              </Button>
            </VStack>
          </Box>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default PitchDeckPage;
